import { useMemo } from 'react';
import moment from 'moment';
import { FinanceTableColumn } from '../types';
import { formatCurrency, formatPercentage } from 'utils';

const formatMoneyValue = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  return formatCurrency(value.toString());
};

export const useFinanceListColumns = () => {
  const columns: FinanceTableColumn[] = useMemo(
    () => [
      {
        header: 'Customer',
        renderKey: 'customerName',
      },
      {
        header: 'Location',
        renderKey: 'locationBranch',
        sortKey: 'location',
      },
      {
        header: 'Sales Rep',
        renderKey: 'salesRep',
        sortKey: 'salesRep',
      },
      {
        header: 'Deal Date',
        renderKey: 'dealDate',
        sortKey: 'dealDate',
        formatter: row => (row.dealDate ? moment(row.dealDate).format('MM/DD/YYYY') : '-'),
      },
      {
        header: 'Deal Amount',
        renderKey: 'dealAmount',
        formatter: row => formatMoneyValue(row.dealAmount),
      },
      {
        header: 'Equipment Sale Price',
        renderKey: 'equipmentSalePrice',
        formatter: row => formatMoneyValue(row.equipmentSalePrice),
      },
      {
        header: 'Equipment Cost',
        renderKey: 'equipmentCost',
        formatter: row => formatMoneyValue(row.equipmentCost),
      },
      {
        header: 'Equipment Gross',
        renderKey: 'equipmentGross',
        formatter: row => formatMoneyValue(row.equipmentGross),
      },
      {
        header: 'Equipment Margin %',
        renderKey: 'equipmentMarginPercent',
        formatter: row => formatPercentage(row.equipmentMarginPercent),
      },
      {
        header: 'Finance Used',
        renderKey: 'financeUsed',
      },
      {
        header: 'Finance Gross',
        renderKey: 'financeGross',
        formatter: row => formatMoneyValue(row.financeGross),
      },
      {
        header: 'Finance Profit',
        renderKey: 'financeProfit',
        formatter: row => formatMoneyValue(row.financeProfit),
      },
      {
        header: 'Finance Margin %',
        renderKey: 'financeMarginPercent',
        formatter: row => formatPercentage(row.financeMarginPercent),
      },
      {
        header: 'Internal Finance Tier',
        renderKey: 'internalFinanceTier',
      },
      {
        header: 'Lender',
        renderKey: 'lender',
      },
      {
        header: 'Doc Fee Amount',
        renderKey: 'docFeeAmount',
      },
      {
        header: 'ESC Sold',
        renderKey: 'escSold',
      },
      {
        header: 'ESC Vendor',
        renderKey: 'escVendor',
      },
      {
        header: 'ESC Gross',
        renderKey: 'escGross',
        formatter: row => formatMoneyValue(row.escGross),
      },
      {
        header: 'ESC Cost',
        renderKey: 'escCost',
        formatter: row => formatMoneyValue(row.escCost),
      },
      {
        header: 'ESC Profit',
        renderKey: 'escProfit',
        formatter: row => formatMoneyValue(row.escProfit),
      },
      {
        header: 'GAP Sold',
        renderKey: 'gapSold',
      },
      {
        header: 'GAP Vendor',
        renderKey: 'gapVendor',
      },
      {
        header: 'GAP Gross',
        renderKey: 'gapGross',
        formatter: row => formatMoneyValue(row.gapGross),
      },
      {
        header: 'GAP Cost',
        renderKey: 'gapCost',
        formatter: row => formatMoneyValue(row.gapCost),
      },
      {
        header: 'GAP Profit',
        renderKey: 'gapProfit',
        formatter: row => formatMoneyValue(row.gapProfit),
      },
      {
        header: 'Insurance Sold',
        renderKey: 'insuranceSold',
      },
      {
        header: 'Insurance Vendor',
        renderKey: 'insuranceVendor',
      },
      {
        header: 'Insurance Gross',
        renderKey: 'insuranceGross',
        formatter: row => formatMoneyValue(row.insuranceGross),
      },
      {
        header: 'Insurance Cost',
        renderKey: 'insuranceCost',
        formatter: row => formatMoneyValue(row.insuranceCost),
      },
      {
        header: 'Insurance Profit',
        renderKey: 'insuranceProfit',
        formatter: row => formatMoneyValue(row.insuranceProfit),
      },
      {
        header: 'Roadside Sold',
        renderKey: 'roadsideSold',
      },
      {
        header: 'Roadside Vendor',
        renderKey: 'roadsideVendor',
      },
      {
        header: 'Roadside Gross',
        renderKey: 'roadsideGross',
        formatter: row => formatMoneyValue(row.roadsideGross),
      },
      {
        header: 'Roadside Cost',
        renderKey: 'roadsideCost',
        formatter: row => formatMoneyValue(row.roadsideCost),
      },
      {
        header: 'Roadside Profit',
        renderKey: 'roadsideProfit',
        formatter: row => formatMoneyValue(row.roadsideProfit),
      },
    ],
    []
  );

  return { columns };
};
